import { FC, FormEvent } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { MinimalChannelResource } from "@/types/resources";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { FormLayout, Label, Larger } from "@/components/Forms";
import Button from "@/components/Button";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { NewDropdown } from "@/components/Forms/Dropdown";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import FileField from "@/components/Forms/FileField";

interface FormDataT {
  channel_id?: number;
  watermark1: File | null;
  watermark2: File | null;
  watermark3: File | null;
}

interface Props {
  onClose: () => void;
  editableChannel?: MinimalChannelResource;
}

const WatermarkForm: FC<Props> = ({ onClose, editableChannel }) => {
  const alerts = useAlerts();

  const { teamId } = useParams();

  const queryClient = useQueryClient();

  const { data: channelOptions } = useQuery(
    ["channels", { teamId, authorized: true, minimal: true }],
    () =>
      api.get<never, MinimalChannelResource[]>(`/v1/channels/authorized`, {
        params: {
          ...(teamId !== "all" && {
            team_id: teamId,
          }),
        },
      }),
    {
      staleTime: 1000 * 60,
      select: (data) =>
        data.map((channel) => ({ label: channel.name, value: channel.id })),
    }
  );

  const methods = useForm<FormDataT>({
    defaultValues: {
      channel_id: editableChannel?.id,
      watermark1: null,
      watermark2: null,
      watermark3: null,
    },
  });

  const {
    handleSubmit,
    setError,
    clearErrors,
    watch,
    control,
    formState: { isSubmitting, errors },
  } = methods;

  const channelId = watch("channel_id");

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const formData = new FormData();

        if (data.watermark1) {
          formData.append("watermark1", data.watermark1);
        }

        if (data.watermark2) {
          formData.append("watermark2", data.watermark2);
        }

        if (data.watermark3) {
          formData.append("watermark3", data.watermark3);
        }

        await api.post<typeof formData, MinimalChannelResource>(
          `/v1/channels/${data.channel_id}/watermark`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        onClose();

        queryClient.invalidateQueries(["watermarks"], {
          exact: false,
        });

        alerts.success(
          editableChannel
            ? "Watermark successfully updated"
            : "New watermark successfully added"
        );
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        onClose={onClose}
        heading={editableChannel ? "Edit" : "Add new"}
      >
        <FormProvider {...methods}>
          <SidePanelBody onSubmit={onSubmit}>
            <SidePanelContent>
              <FormLayout>
                <Larger>
                  <Label>Channel</Label>
                  <NewDropdown<number>
                    disabled={!!editableChannel}
                    control={control}
                    name="channel_id"
                    options={channelOptions ?? []}
                  />
                  {!!errors.channel_id && (
                    <ValidationMessage>
                      {errors.channel_id.message}
                    </ValidationMessage>
                  )}
                </Larger>
                <Larger>
                  <FileField
                    fileType="image"
                    label="Watermark"
                    subLabel="(1:1 ratio)"
                    name="watermark1"
                    notes="PNG, max 2MB"
                    existingMedia={editableChannel?.watermarks?.find(
                      (m) => m.meta?.ratio === "1:1"
                    )}
                  />
                </Larger>
                <Larger>
                  <FileField
                    fileType="image"
                    label="Watermark"
                    subLabel="(4:5 ratio)"
                    name="watermark2"
                    notes="PNG, max 2MB"
                    existingMedia={editableChannel?.watermarks?.find(
                      (m) => m.meta?.ratio === "4:5"
                    )}
                  />
                </Larger>
                <Larger>
                  <FileField
                    fileType="image"
                    label="Watermark"
                    subLabel="(9:16 ratio)"
                    name="watermark3"
                    notes="PNG, max 2MB"
                    existingMedia={editableChannel?.watermarks?.find(
                      (m) => m.meta?.ratio === "9:16"
                    )}
                  />
                </Larger>
              </FormLayout>
            </SidePanelContent>
            <SidePanelFooter $actions>
              <Button
                disabled={isSubmitting || !channelId}
                loading={isSubmitting}
                type="submit"
              >
                {editableChannel ? "Save" : "Add"}
              </Button>
              <Button color="secondary" onClick={onClose} type="button">
                Cancel
              </Button>
            </SidePanelFooter>
          </SidePanelBody>
        </FormProvider>
      </SidePanel>
    </Modal>
  );
};

export default WatermarkForm;
